/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import { Link } from "react-router";
import { observer } from "mobx-react";
import styled from "styled-components";
import { ArrowLeft, ChevronsLeft, ChevronsRight } from "lucide-react";
// ChevronRight (the group rows' trailing ">") and House (the old home icon) are no longer used.
// import { ArrowLeft, ChevronRight, House } from "lucide-react";

import Logo from "@src/components/ui/Logo";
// The user menu moved to the top bar (avatar initial, as in the Ops Console).
// import UserDropdown from "@src/components/ui/Dropdowns/UserDropdown";
// import AboutModal from "@src/components/smart/AboutModal";
import userStore from "@src/stores/UserStore";
import configLoader from "@src/utils/Config";

import { ThemePalette } from "@src/components/Theme";
import shellStore from "./shellStore";
import {
  HUB_ID,
  pathSection,
  sectionFor,
  sectionPath,
  visibleSections,
  type Section,
} from "./sections";
import { recordSectionVisit } from "./recentSections";

/*
 * The Swift sidebar, in the AWS Management module's pattern (layout/Sidebar.tsx), as the Ops
 * Console has it (admin/sidebar.js): the lockup with the current project under "Swift", "Back to
 * Tech9IQ", then one list at a time - the sections, flat, or the pages of a group once it is
 * entered ("← All sections", the group's name, its pages); the signed-in user at the foot.
 * The << / >> button at its foot folds it to a 56 px rail: the Tech9IQ mark and the sections'
 * icons, each with its name as a tooltip. On a phone it is always the rail; >> opens it over the page.
 */
const WIDTH = 288;
const RAIL = 56;
const PHONE = 720;

const Rail = styled.div<{ folded: boolean }>`
  position: relative;
  flex-shrink: 0;
  height: 100%;
  width: ${props => (props.folded ? RAIL : WIDTH)}px;
  min-width: ${props => (props.folded ? RAIL : WIDTH)}px;
  z-index: 400;
`;
const Wrapper = styled.nav`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${WIDTH}px;
  box-sizing: border-box;
  background: ${ThemePalette.surface};
  border-right: 1px solid ${ThemePalette.grayscale[0]};
  font-size: 14px;
  line-height: 20px;

  a,
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    text-align: left;
    cursor: pointer;
    text-decoration: none;
  }
  a:focus-visible,
  button:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: -2px;
  }

  .sb-brand {
    display: flex;
    align-items: center;
    height: 56px;
    padding: 0 16px;
    flex-shrink: 0;
    box-sizing: border-box;
    border-bottom: 1px solid ${ThemePalette.grayscale[0]};
  }
  .sb-brand-mark {
    display: none;
  }
  .sb-top {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    flex-shrink: 0;
    box-sizing: border-box;
    border-bottom: 1px solid ${ThemePalette.grayscale[0]};
    color: ${ThemePalette.grayscale[4]};
    white-space: nowrap;
    &:hover {
      background: var(--t9-bg-hover);
      color: ${ThemePalette.black};
    }
  }
  .sb-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px 0;
  }
  .sb-group {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    box-sizing: border-box;
    font-weight: 500;
    color: var(--t9-text-body);
    white-space: nowrap;
    svg {
      flex-shrink: 0;
      color: ${ThemePalette.grayscale[3]};
    }
    &:hover {
      background: var(--t9-bg-hover);
    }
  }
  .sb-more {
    margin-left: auto;
  }
  .sb-back {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    box-sizing: border-box;
    color: ${ThemePalette.grayscale[5]};
    white-space: nowrap;
    &:hover {
      background: var(--t9-bg-hover);
      color: ${ThemePalette.black};
    }
  }
  .sb-group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px 6px 16px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    letter-spacing: 0.025em;
    text-transform: uppercase;
    color: ${ThemePalette.grayscale[5]};
    white-space: nowrap;
    svg {
      flex-shrink: 0;
      color: ${ThemePalette.grayscale[3]};
    }
  }
  .sb-item {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    box-sizing: border-box;
    color: ${ThemePalette.grayscale[4]};
    white-space: nowrap;
    svg {
      flex-shrink: 0;
      color: ${ThemePalette.grayscale[3]};
    }
    &:hover {
      background: var(--t9-bg-hover);
      color: ${ThemePalette.black};
    }
  }
  .sb-label {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .is-active,
  .is-active:hover {
    background: ${ThemePalette.brandSoft};
    color: ${ThemePalette.primaryLight};
    font-weight: 500;
    svg {
      color: ${ThemePalette.primary};
    }
  }
  .sb-footer {
    position: relative;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    padding: 8px;
    border-top: 1px solid ${ThemePalette.grayscale[0]};
  }
  .sb-collapse {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    color: ${ThemePalette.grayscale[5]};
    &:hover {
      background: var(--t9-bg-hover);
      color: ${ThemePalette.black};
    }
  }

  /* Folded: a rail of icons, opening back out over the page on hover or focus. */
  &.folded {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${RAIL}px;
    overflow: hidden;
    transition:
      width 160ms ease,
      box-shadow 160ms ease;
  }
  /* Was: the rail opened out on hover or focus (&.folded:hover, &.folded:focus-within). Now it
     stays a rail with tooltips; on a phone the >> button opens it over the page (.peek). */
  &.folded.peek {
    width: ${WIDTH}px;
    box-shadow: var(--t9-shadow-xl);
  }
  &.folded:not(.peek) {
    .sb-brand {
      padding: 0;
      justify-content: center;
    }
    .sb-brand-full,
    .sb-label,
    .sb-more,
    .sb-group-head span,
    [data-fold="hide"] {
      display: none;
    }
    .sb-brand-mark {
      display: block;
    }
    .sb-top,
    .sb-group,
    .sb-back,
    .sb-item,
    .sb-group-head {
      padding-left: 20px;
      padding-right: 0;
    }
    .sb-footer {
      padding: 8px 6px;
      justify-content: center;
    }
    [data-fold="center"] {
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    &.folded {
      transition: none;
    }
  }
`;

type Props = {
  currentPage?: string;
  className?: string;
  /** Always folded (the rail of icons), whatever the top bar's menu button says. */
  collapsed?: boolean;
  hideLogos?: boolean;
};
type State = {
  /** "auto" follows the page; "home" is "← All sections" pressed; otherwise an entered group. */
  view: string;
  phone: boolean;
  /** Phone only: the rail opened over the page. */
  peek: boolean;
};

@observer
class Navigation extends React.Component<Props, State> {
  state: State = {
    view: "auto",
    phone: typeof window !== "undefined" && window.innerWidth <= PHONE,
    peek: false,
  };

  firstRowRef = React.createRef<HTMLButtonElement>();

  resizeTimeout: number | null = null;

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    const section = sectionFor(this.currentPage);
    const user = userStore.loggedUser;
    if (section && section.id !== HUB_ID && user) {
      recordSectionVisit(user.id, section.id);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeTimeout) window.clearTimeout(this.resizeTimeout);
  }

  handleResize = () => {
    if (this.resizeTimeout) return;
    this.resizeTimeout = window.setTimeout(() => {
      this.resizeTimeout = null;
      const phone = window.innerWidth <= PHONE;
      if (phone !== this.state.phone) this.setState({ phone });
    }, 100);
  };

  get currentPage(): string {
    if (this.props.currentPage != null) return this.props.currentPage;
    return pathSection(window.location.pathname) || HUB_ID;
  }

  get sections(): Section[] {
    const isAdmin = Boolean(userStore.loggedUser && userStore.loggedUser.isAdmin);
    const disabled = configLoader.config ? configLoader.config.disabledPages : [];
    return visibleSections(isAdmin, disabled);
  }

  enterGroup(id: string) {
    this.setState({ view: id }, () => this.firstRowRef.current?.focus());
  }

  // The user menu (About, Sign out) is in the top bar now.
  // handleUserItemClick(item: { value: string }) {
  //   if (item.value === "about") this.setState({ showAbout: true });
  //   else if (item.value === "signout") userStore.logout();
  // }

  toggleFold(folded: boolean) {
    if (this.state.phone) this.setState(prev => ({ peek: !prev.peek }));
    else shellStore.setCollapsed(!folded);
  }

  renderHome(sections: Section[], active?: Section) {
    return sections.map((section, i) => {
      const isActive = active?.id === section.id;
      const cls = `sb-group${isActive ? " is-active" : ""}`;
      const Icon = section.icon;
      const content = (
        <>
          <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
          <span className="sb-label">{section.label}</span>
        </>
      );
      if (section.items.length > 1) {
        return (
          <button
            key={section.id}
            type="button"
            className={cls}
            aria-label={section.label}
            title={section.label}
            aria-expanded={false}
            ref={i === 0 ? this.firstRowRef : undefined}
            onClick={() => this.enterGroup(section.id)}
          >
            {content}
            {/* No trailing ">" (Tech9IQ shell): entering the group is shown by the highlight. */}
            {/* <ChevronRight className="sb-more" size={14} strokeWidth={2} aria-hidden="true" /> */}
          </button>
        );
      }
      return (
        <Link
          key={section.id}
          to={sectionPath(section)}
          className={cls}
          aria-label={section.label}
          title={section.label}
          aria-current={isActive ? "page" : undefined}
        >
          {content}
        </Link>
      );
    });
  }

  renderGroup(group: Section) {
    const page = this.currentPage;
    const Icon = group.icon;
    return (
      <>
        <button
          type="button"
          className="sb-back"
          ref={this.firstRowRef}
          aria-label="All sections"
          title="All sections"
          onClick={() => this.setState({ view: "home" }, () => this.firstRowRef.current?.focus())}
        >
          <ArrowLeft size={14} strokeWidth={2} aria-hidden="true" />
          <span className="sb-label">All sections</span>
        </button>
        <div className="sb-group-head" id={`sb-head-${group.id}`}>
          <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
          <span>{group.label}</span>
        </div>
        <div role="group" aria-labelledby={`sb-head-${group.id}`}>
          {group.items.map(item => {
            const isActive = item.value === page || (page === "streamlog" && item.value === "logging");
            const ItemIcon = item.icon;
            return (
              <Link
                key={item.value}
                to={`/${item.value}`}
                className={`sb-item${isActive ? " is-active" : ""}`}
                aria-label={item.label}
                title={item.label}
                aria-current={isActive ? "page" : undefined}
              >
                <ItemIcon size={16} strokeWidth={1.75} aria-hidden="true" />
                <span className="sb-label">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </>
    );
  }

  render() {
    const folded = Boolean(this.props.collapsed) || shellStore.collapsed || this.state.phone;
    const peek = this.state.phone && this.state.peek;
    const foldLabel = folded && !peek ? "Expand the sidebar" : "Collapse the sidebar";
    const sections = this.sections;
    const active = sectionFor(this.currentPage);
    const { view } = this.state;
    const autoGroup = active && active.items.length > 1 ? active : undefined;
    const group =
      view === "home"
        ? undefined
        : view === "auto"
          ? autoGroup && sections.find(s => s.id === autoGroup.id)
          : sections.find(s => s.id === view);
    const user = userStore.loggedUser;
    const project = user && user.project ? user.project.name : null;
    const homeUrl = configLoader.config ? configLoader.config.tech9iqHomeUrl : "";
    // One wording across the Tech9IQ modules: "Back to Ops Console", or "Back to portal" for a customer.
    const configured = (configLoader.config && configLoader.config.tech9iqHomeLabel) || "";
    const homeLabel = /portal/i.test(configured) ? "Back to portal" : "Back to Ops Console";

    return (
      <Rail folded={folded}>
        <Wrapper
          id="swift-sidebar"
          aria-label="SwiftIQ"
          className={`${folded ? "folded " : ""}${peek ? "peek " : ""}${this.props.className || ""}`}
        >
          {this.props.hideLogos ? null : (
            <div className="sb-brand">
              <Logo className="sb-brand-full" to="/" small subtitle={project} />
              <Logo className="sb-brand-mark" to="/" mark />
            </div>
          )}
          {homeUrl ? (
            <a className="sb-top" href={homeUrl} aria-label={homeLabel} title={homeLabel}>
              <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
              <span className="sb-label">{homeLabel}</span>
            </a>
          ) : null}
          <div className="sb-scroll">
            {group ? this.renderGroup(group) : this.renderHome(sections, active)}
          </div>
          <div className="sb-footer">
            {/* Was the signed-in user (UserDropdown); now in the top bar. The fold button lives here. */}
            <button
              type="button"
              className="sb-collapse"
              aria-label={foldLabel}
              title={foldLabel}
              aria-expanded={!folded || peek}
              aria-controls="swift-sidebar"
              onClick={() => this.toggleFold(folded)}
            >
              {folded && !peek ? (
                <ChevronsRight size={16} strokeWidth={2} aria-hidden="true" />
              ) : (
                <ChevronsLeft size={16} strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          </div>
        </Wrapper>
      </Rail>
    );
  }
}

export default Navigation;
